import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import CreatableSelect from 'react-select/creatable';

import ImageUpload from '@components/ImageUpload';
import { toastError, toastSuccess } from '@components/Toast';

import { getCategories as fetchCategories, getMarkets as fetchMarkets } from '@store/design/action';
import { fetchGarmentsDetails } from '@store/material/action';
import { getAllCategories, getAllMarkets, getAllTags, getMaterialsDetails } from '@store/reducers';
import { fetchAllTags } from '@store/supplier/action';

import ApiClient from '@lib/apiClient';

import { MATERIAL_TYPE } from '@/constants/constant';

const Garments = ({ materialData, onclose, viewType, renderFrom, saveEventLog }) => {
	const [show, setShow] = useState(true);
	const [selectedItem, setSelecteItem] = useState(null);
	const [imageData, setImageData] = useState(null);
	const [, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [materialId, setMaterialId] = useState('');
	const [errors, setErrors] = useState({});
	const dispatch = useDispatch();
	const tagType = 'MATERIAL_SUSTAINABLE_TAG';
	const { id } = useParams();
	const allMarkets = useSelector(getAllMarkets);
	const allCategories = useSelector(getAllCategories);
	let materialDetails = useSelector(getMaterialsDetails);
	let allTags = useSelector(getAllTags);
	allTags = allTags?.materialTags?.map((item) => {
		return {
			id: item.id,
			text: item.text,
			value: item.id,
			label: item.text
		};
	});

	let selectedStrengthList = selectedItem?.tagRequestList?.map((item) => {
		return {
			id: item.id,
			text: item.text,
			value: item.text,
			label: item.text
		};
	});

	useEffect(() => {
		setIsLoading(true);
		dispatch(fetchAllTags('', tagType));
		dispatch(fetchCategories()).finally(() => {
			setIsLoading(false);
		});

		dispatch(fetchMarkets()).finally(() => {
			setIsLoading(false);
		});
		if (viewType === 'EDIT' && materialData) {
			dispatch(fetchGarmentsDetails(materialData?.materialId)).finally(() => {
				setIsLoading(false);
			});
		}
	}, [materialData]);

	useEffect(() => {
		if (viewType === 'EDIT' && materialData) {
			const itemDetails = materialDetails?.data;
			setMaterialId(itemDetails?.id);
			setImageData({ documentDTO: { documentPath: itemDetails?.documentPath } });
			setSelecteItem({
				...selectedItem,
				name: itemDetails?.name || '',
				marketId: itemDetails?.marketResponse?.id || null,
				categoryId: itemDetails?.categoryResponse?.id || null,
				description: itemDetails?.description || '',
				documentId: itemDetails?.documentId || null,
				materialType: 'GARMENT',
				tagRequestList: itemDetails?.tagResponseList?.map((item) => {
					return {
						id: item.id,
						text: item.text,
						value: item.id,
						label: item.text
					};
				})
			});
		}
	}, [materialDetails]);

	const handleClose = () => {
		setShow(false);
		onclose();
	};
	const onAdd = async (data) => {
		setIsLoading(true);
		await ApiClient.POST('uploadDocument', data)
			.then(({ data }) => {
				setIsLoading(false);
				if (data) {
					setImageData({ documentDTO: data });
					setSelecteItem({ ...selectedItem, documentId: data.id });
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onUploadImage = (image) => {
		setImageData({ documentDTO: image });
		onAdd(image);
	};

	const onChangeTags = (selected) => {
		let tags = selected.map((item) => {
			if (item.__isNew__) {
				return {
					text: item.label
				};
			} else {
				return {
					text: item.label,
					id: item.id
				};
			}
		});

		setSelecteItem({
			...selectedItem,
			tagRequestList: [...tags]
		});
	};

	const isValidForm = (body) => {
		if (body.name?.trim() === '' || !body.name) {
			setErrors({ ...errors, nameError: 'Product name is required' });
			return false;
		}
		if (!body.categoryId || body.categoryId === '') {
			setErrors({ ...errors, categoryTypeError: 'Category is required' });
			return false;
		}
		if (!body.marketId || body.marketId === '') {
			setErrors({ ...errors, marketTypeError: 'Market is required' });
			return false;
		}
		return true;
	};

	const addNewGarments = async (body) => {
		const isValid = isValidForm(body);
		if (!isValid) return;
		setIsSubmitLoading(true);
		await ApiClient.POST('addNewGarments', { ...body, materialType: MATERIAL_TYPE.GARMENT })
			.then(({ data }) => {
				setIsSubmitLoading(false);
				if (data && data.success) {
					if (renderFrom && renderFrom === 'SUPPLIER_ADD') {
						saveEventLog(id, body.name);
					} else if (renderFrom && renderFrom === 'MATERIAL_ADD') {
						saveEventLog(body.name, MATERIAL_TYPE.GARMENT);
					}
					toastSuccess(data.message);
					handleClose();
				}
			})
			.catch(({ response }) => {
				setIsSubmitLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const updateNewGarments = async (body) => {
		const isValid = isValidForm(body);
		if (!isValid) return;
		setIsSubmitLoading(true);
		await ApiClient.PUT('updateGarments', body, materialId)
			.then(({ data }) => {
				setIsSubmitLoading(false);
				if (data && data.success) {
					if (renderFrom && renderFrom === 'MATERIAL_EDIT') {
						saveEventLog(body.name);
					}
					toastSuccess(data.message);
					handleClose();
				}
			})
			.catch(({ response }) => {
				setIsSubmitLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const onSubmit = () => {
		let body = { ...selectedItem };
		if (viewType === 'ADD') {
			addNewGarments(body);
		}
		if (viewType === 'EDIT') {
			updateNewGarments(body);
		}
	};

	return (
		<Modal show={show} onHide={handleClose} backdrop="static" centered className="width-784-popup">
			<div className="common-popup width-784">
				<Modal.Header closeButton>
					<Modal.Title>
						<div className="popup-tilte">
							<h3 className="semibold-16 mb-0">Garments material</h3>
						</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="common-popup-body">
						<div className="container px-0">
							<div className="row">
								<div className="col-md-3">
									<div className="upload-section">
										{imageData?.documentDTO ? (
											<>
												<img
													className="border-0"
													src={
														imageData.documentDTO.base64Str
															? imageData.documentDTO.base64Str
															: imageData.documentDTO.documentPath ||
																imageData.documentDTO.docUrl
													}
													alt=""
												/>
												<ImageUpload
													size="sm"
													onUploadImage={onUploadImage}
													docType={'MATERIAL_DESIGN'}
													type={'image'}
													isEditable={true}
												/>
											</>
										) : (
											<ImageUpload
												size="sm"
												onUploadImage={onUploadImage}
												docType={'MATERIAL_DESIGN'}
												type={'image'}
											/>
										)}
									</div>
								</div>
								<div className="col-md-9">
									<div className="row">
										<div className="col-md-12">
											<h3 className="semibold-16">Material info</h3>
										</div>
										<div className="col-md-12">
											<div className="form-group ">
												<label htmlFor="pname">Material name*</label>
												<input
													type="text"
													autoComplete="off"
													id="pname"
													value={selectedItem?.name}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															name: e.target.value
														})
													}
												/>
												{errors.nameError && (
													<p className="error">{errors.nameError}</p>
												)}
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group ">
												<label htmlFor="market">Market*</label>

												<select
													className="w-100"
													name="market"
													id="market"
													value={selectedItem?.marketId}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															marketId: e.target.value
														})
													}
												>
													<option value={''}>Select</option>
													{allMarkets?.map((item, idx) => (
														<option value={item.id} key={idx}>
															{item.name}
														</option>
													))}
												</select>
												{errors.marketTypeError && (
													<p className="error">{errors.marketTypeError}</p>
												)}
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group ">
												<label htmlFor="category">Category*</label>

												<select
													className="w-100"
													name="category"
													id="category"
													value={selectedItem?.categoryId}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															categoryId: e.target.value
														})
													}
												>
													<option value={''}>Select</option>
													{allCategories?.map((item, idx) => (
														<option value={item.id} key={idx}>
															{item.name}
														</option>
													))}
												</select>
												{errors.categoryTypeError && (
													<p className="error">{errors.categoryTypeError}</p>
												)}
											</div>
										</div>
										<div className="col-md-12">
											<h3 className="semibold-16">Others info</h3>
										</div>
										<div className="col-md-12">
											<div className="form-group no-shadow-react-select size36">
												<label htmlFor="strengthList">Sustainable</label>
												<CreatableSelect
													value={selectedStrengthList}
													isMulti
													onChange={onChangeTags}
													options={allTags}
												/>
											</div>
										</div>
										<div className="col-md-12">
											<div className="form-group mb-0">
												<label htmlFor="description">Description</label>
												<textarea
													name="description"
													id="description"
													placeholder="Write description"
													defaultValue={''}
													value={selectedItem?.description}
													onChange={(e) =>
														setSelecteItem({
															...selectedItem,
															description: e.target.value
														})
													}
												/>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<div className="footer-buttons text-right">
						<button className="button outline size32" onClick={handleClose}>
							Cancel
						</button>
						<button
							disabled={isSubmitLoading}
							className="button size32 ml-2"
							onClick={() => onSubmit()}
						>
							Submit
						</button>
					</div>
				</Modal.Footer>
			</div>
		</Modal>
	);
};

export default Garments;
